/**
 * 财务对账页（C7 占位 UI）。
 *
 * Phase 4-5 / Stream 4 / Plan §12 / Design §5.9.
 *
 * ⚠️ V1 占位：
 *  - 上传 CSV：仅录入文件元数据（fileName/billMonth/fileSize/notes），不实际上传文件
 *  - 触发 diff：占位按钮，后端 flip uploaded→diff_done + 假 summary
 *  - 不一致清单：占位空表（真实 mismatch 检测在另立 spec）
 *  - 人工 mark：真实可用（写 reconciliation_marks 表）
 *  - 月报导出：占位 CSV（batch metadata + marks）
 *
 * 完整 CSV 解析 + diff 算法详细 spec：
 * project-docs/specs/2026-05-23-finance-reconciliation-design.md（待补）
 *
 * 权限：
 *  - reconciliation:read    (super_admin/finance) — 查看
 *  - reconciliation:upload  (super_admin/finance) — 上传 + 触发 diff
 *  - reconciliation:mark    (super_admin/finance) — 添加标记
 */
import { useMemo, useState } from 'react';
import {
  Card, Table, Form, Input, InputNumber, Button, Space, Tag, Modal, Alert,
  message, Select, Drawer, Descriptions, Statistic, Row, Col, Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listBatches, getBatchDetail, uploadBatch, triggerDiff, addMark, batchExportUrl,
  type ReconciliationBatchDto, type ReconciliationStatus,
  type MarkRecordType, type ListBatchesFilter,
} from '../../api/reconciliation';

function formatTs(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function statusColor(s: ReconciliationStatus): string {
  switch (s) {
    case 'uploaded': return 'blue';
    case 'diff_done': return 'green';
    case 'diff_failed': return 'red';
  }
}

function statusLabel(s: ReconciliationStatus): string {
  switch (s) {
    case 'uploaded': return '已上传';
    case 'diff_done': return '已对账';
    case 'diff_failed': return '对账失败';
  }
}

function ReconciliationPageInner() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<{ billMonth?: string; status?: ReconciliationStatus }>();
  const [filters, setFilters] = useState<ListBatchesFilter>({});

  // upload modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm] = Form.useForm<{
    billMonth: string; fileName: string; fileSize: number; notes?: string;
  }>();

  // detail drawer + mark
  const [drawerBatchId, setDrawerBatchId] = useState<string | null>(null);
  const [markForm] = Form.useForm<{
    recordId: string; recordType: MarkRecordType; markReason: string;
  }>();

  const listQuery = useQuery({
    queryKey: ['reconciliation', filters],
    queryFn: () => listBatches(filters),
  });

  const detailQuery = useQuery({
    queryKey: ['reconciliation', 'detail', drawerBatchId],
    queryFn: () => getBatchDetail(drawerBatchId!),
    enabled: drawerBatchId !== null,
  });

  const errorHandler = (label: string) =>
    (e: { response?: { data?: { message?: string } }; message?: string }) =>
      message.error(e.response?.data?.message ?? e.message ?? label);

  const uploadMutation = useMutation({
    mutationFn: uploadBatch,
    onSuccess: (data) => {
      message.success(`已创建批次 ${data.batchId}（占位实现 — 仅记录元数据）`);
      setUploadOpen(false);
      uploadForm.resetFields();
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
    },
    onError: errorHandler('上传失败'),
  });

  const triggerDiffMutation = useMutation({
    mutationFn: triggerDiff,
    onSuccess: (data) => {
      message.success(
        `已触发 diff（占位实现 — summary 为 0/0/0；真实计算待 spec）：${data.batchId}`,
      );
      // 失效列表 + drawer detail（防 drawer 内 diffSummary 不实时刷新）
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
      qc.invalidateQueries({ queryKey: ['reconciliation', 'detail'] });
    },
    onError: errorHandler('触发 diff 失败'),
  });

  const addMarkMutation = useMutation({
    mutationFn: (vars: {
      batchId: string;
      recordId: string;
      recordType: MarkRecordType;
      markReason: string;
    }) => addMark(vars.batchId, {
      recordId: vars.recordId, recordType: vars.recordType, markReason: vars.markReason,
    }),
    onSuccess: () => {
      message.success('已添加 mark');
      markForm.resetFields();
      qc.invalidateQueries({ queryKey: ['reconciliation', 'detail'] });
    },
    onError: errorHandler('添加 mark 失败'),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListBatchesFilter = {};
    if (v.billMonth?.trim()) next.billMonth = v.billMonth.trim();
    if (v.status) next.status = v.status;
    setFilters(next);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilters({});
  }

  function submitUpload() {
    uploadForm.validateFields().then((v) => {
      uploadMutation.mutate({
        billMonth: v.billMonth,
        fileName: v.fileName,
        fileSize: v.fileSize,
        notes: v.notes?.trim() ? v.notes.trim() : null,
      });
    }).catch(() => {
      // 校验失败：antd 已在表单内联显示字段错误，无需额外处理（避免 unhandled rejection）
    });
  }

  function submitAddMark() {
    if (!drawerBatchId) return;
    markForm.validateFields().then((v) => {
      addMarkMutation.mutate({
        batchId: drawerBatchId,
        recordId: v.recordId.trim(),
        recordType: v.recordType,
        markReason: v.markReason.trim(),
      });
    }).catch(() => {
      // 校验失败：antd 已在表单内联显示字段错误，无需额外处理（避免 unhandled rejection）
    });
  }

  const aggregate = useMemo(() => {
    const items = listQuery.data?.items ?? [];
    const uploaded = items.filter((b) => b.status === 'uploaded').length;
    const done = items.filter((b) => b.status === 'diff_done').length;
    const totalMismatched = items.reduce(
      (acc, b) => acc + (b.diffSummary?.mismatched ?? 0), 0,
    );
    return { uploaded, done, totalMismatched };
  }, [listQuery.data]);

  const columns: ColumnsType<ReconciliationBatchDto> = [
    { title: 'batchId', dataIndex: 'batchId', key: 'bid', width: 220, ellipsis: true },
    { title: '账单月', dataIndex: 'billMonth', key: 'bm', width: 100 },
    { title: '文件名', dataIndex: 'fileName', key: 'fn', width: 200, ellipsis: true },
    {
      title: '大小', dataIndex: 'fileSize', key: 'fs', width: 100,
      render: (v: number) => formatBytes(v),
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 110,
      render: (s: ReconciliationStatus) => <Tag color={statusColor(s)}>{statusLabel(s)}</Tag>,
    },
    {
      title: 'diff (匹配/不匹配/总)',
      key: 'diff', width: 180,
      render: (_: unknown, row) => row.diffSummary
        ? `${row.diffSummary.matched} / ${row.diffSummary.mismatched} / ${row.diffSummary.total}`
        : '-',
    },
    { title: '上传', dataIndex: 'uploadedAt', key: 'ua', width: 170, render: formatTs },
    { title: '上传人', dataIndex: 'uploadedBy', key: 'ub', width: 130 },
    {
      title: '操作', key: 'actions', width: 220,
      render: (_: unknown, row) => (
        <Space size="small" wrap>
          <Button
            size="small"
            onClick={() => setDrawerBatchId(row.batchId)}
            data-testid={`batch-detail-${row.batchId}`}
          >
            详情
          </Button>
          {row.status === 'uploaded' && (
            <PermissionGate point={PERMISSIONS.RECONCILIATION_UPLOAD}>
              <Button
                size="small" type="primary"
                loading={triggerDiffMutation.isPending && triggerDiffMutation.variables === row.batchId}
                onClick={() => triggerDiffMutation.mutate(row.batchId)}
                data-testid={`batch-trigger-${row.batchId}`}
              >
                触发 diff
              </Button>
            </PermissionGate>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title="财务对账（C7 — 占位 UI）" data-testid="reconciliation-page">
      <Alert
        type="warning" showIcon
        message="V1 占位实现"
        description={
          <span>
            当前 C7 仅完成元数据 CRUD + 占位 trigger diff + 人工 mark；
            <strong>不解析 CSV，不计算真实 diff</strong>。
            完整 CSV 解析 + diff 算法详细 spec 另立：
            <code>project-docs/specs/2026-05-23-finance-reconciliation-design.md</code>（待补）。
          </span>
        }
        style={{ marginBottom: 16 }}
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}><Card size="small"><Statistic title="待对账批次" value={aggregate.uploaded} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="已对账批次" value={aggregate.done} /></Card></Col>
        <Col span={8}><Card size="small"><Statistic title="累计不匹配条目" value={aggregate.totalMismatched} /></Card></Col>
      </Row>

      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Form
          form={filterForm} layout="inline"
          data-testid="reconciliation-filter-form"
          onFinish={applyFilters}
        >
          <Form.Item name="billMonth" label="账单月">
            <Input style={{ width: 130 }} placeholder="YYYY-MM" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="任意" allowClear style={{ width: 130 }}
              options={[
                { value: 'uploaded', label: '已上传' },
                { value: 'diff_done', label: '已对账' },
                { value: 'diff_failed', label: '对账失败' },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">应用</Button>
              <Button onClick={resetFilters}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        <PermissionGate point={PERMISSIONS.RECONCILIATION_UPLOAD}>
          <Button
            type="primary"
            onClick={() => setUploadOpen(true)}
            data-testid="reconciliation-upload-btn"
          >
            上传对账批次
          </Button>
        </PermissionGate>
      </Space>

      <Table<ReconciliationBatchDto>
        rowKey="batchId"
        loading={listQuery.isLoading}
        dataSource={listQuery.data?.items ?? []}
        columns={columns}
        pagination={false}
        data-testid="reconciliation-table"
      />

      {/* upload modal */}
      <Modal
        title="上传对账批次（占位）"
        open={uploadOpen}
        onCancel={() => { setUploadOpen(false); uploadForm.resetFields(); }}
        onOk={submitUpload}
        confirmLoading={uploadMutation.isPending}
        destroyOnClose
        width={560}
        data-testid="reconciliation-upload-modal"
      >
        <Alert
          type="info" showIcon
          message="占位 — 仅录入文件元数据，不实际上传文件二进制。真实上传 + GBK CSV 解析待另立 spec。"
          style={{ marginBottom: 16 }}
        />
        <Form form={uploadForm} layout="vertical" preserve={false}>
          <Form.Item
            label="账单月（YYYY-MM）"
            name="billMonth"
            rules={[
              { required: true, message: '请填账单月' },
              { pattern: /^\d{4}-(0[1-9]|1[0-2])$/, message: '格式必须为 YYYY-MM' },
            ]}
          >
            <Input placeholder="例 2026-05" />
          </Form.Item>
          <Form.Item
            label="文件名"
            name="fileName"
            rules={[
              { required: true, message: '请填文件名' },
              { max: 256, message: '最多 256 字符' },
            ]}
          >
            <Input placeholder="例 wechatpay-bill-202605.csv" />
          </Form.Item>
          <Form.Item
            label="文件大小（字节）"
            name="fileSize"
            rules={[{ required: true, message: '请填文件大小' }]}
          >
            <InputNumber min={1} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="备注" name="notes" rules={[{ max: 512 }]}>
            <Input.TextArea rows={2} maxLength={512} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* detail drawer */}
      <Drawer
        title={`批次详情 ${drawerBatchId ?? ''}`}
        open={drawerBatchId !== null}
        onClose={() => setDrawerBatchId(null)}
        width={720}
        data-testid="reconciliation-detail-drawer"
        extra={
          drawerBatchId ? (
            <a href={batchExportUrl(drawerBatchId)} target="_blank" rel="noreferrer">
              <Button data-testid="batch-export-btn">导出 CSV（占位）</Button>
            </a>
          ) : null
        }
      >
        {detailQuery.data ? (
          <>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="batchId" span={2}>{detailQuery.data.batch.batchId}</Descriptions.Item>
              <Descriptions.Item label="账单月">{detailQuery.data.batch.billMonth}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={statusColor(detailQuery.data.batch.status)}>{statusLabel(detailQuery.data.batch.status)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="文件名" span={2}>{detailQuery.data.batch.fileName}</Descriptions.Item>
              <Descriptions.Item label="文件大小">{formatBytes(detailQuery.data.batch.fileSize)}</Descriptions.Item>
              <Descriptions.Item label="hash">{detailQuery.data.batch.fileHash ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="上传时间">{formatTs(detailQuery.data.batch.uploadedAt)}</Descriptions.Item>
              <Descriptions.Item label="上传人">{detailQuery.data.batch.uploadedBy}</Descriptions.Item>
              {detailQuery.data.batch.diffSummary && (
                <>
                  <Descriptions.Item label="diff 触发时间">{formatTs(detailQuery.data.batch.diffTriggeredAt)}</Descriptions.Item>
                  <Descriptions.Item label="diff 触发人">{detailQuery.data.batch.diffTriggeredBy ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label="diff 匹配">{detailQuery.data.batch.diffSummary.matched}</Descriptions.Item>
                  <Descriptions.Item label="diff 不匹配">{detailQuery.data.batch.diffSummary.mismatched}</Descriptions.Item>
                  <Descriptions.Item label="diff 总数" span={2}>{detailQuery.data.batch.diffSummary.total}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="备注" span={2}>{detailQuery.data.batch.notes ?? '-'}</Descriptions.Item>
            </Descriptions>

            <h4>不一致清单（V1 占位 — 真实 mismatch 检测待 spec）</h4>
            <Empty description="V1 不展示真实 mismatch 数据；详细 spec 落地后接入" />

            <h4 style={{ marginTop: 16 }}>人工标记 ({detailQuery.data.marks.length})</h4>
            <Table
              rowKey="markId"
              dataSource={detailQuery.data.marks}
              pagination={false}
              size="small"
              data-testid="batch-marks-table"
              columns={[
                { title: 'recordId', dataIndex: 'recordId', key: 'rid', width: 160 },
                {
                  title: 'recordType', dataIndex: 'recordType', key: 'rt', width: 100,
                  render: (t: MarkRecordType) => <Tag>{t}</Tag>,
                },
                { title: 'reason', dataIndex: 'markReason', key: 'rs', ellipsis: true },
                { title: 'by', dataIndex: 'markedBy', key: 'mb', width: 130 },
                { title: 'at', dataIndex: 'markedAt', key: 'ma', width: 170, render: formatTs },
              ]}
            />

            <PermissionGate point={PERMISSIONS.RECONCILIATION_MARK}>
              <Card size="small" title="添加人工标记" style={{ marginTop: 16 }}>
                <Form form={markForm} layout="vertical" preserve={false}>
                  <Form.Item
                    label="recordId（微信对账行 ID / 内部 payment_id 等）"
                    name="recordId"
                    rules={[{ required: true, message: '请填 recordId' }]}
                  >
                    <Input placeholder="例 wx_tx_4200001234567890" />
                  </Form.Item>
                  <Form.Item
                    label="recordType"
                    name="recordType"
                    rules={[{ required: true, message: '请选 recordType' }]}
                  >
                    <Select
                      options={[
                        { value: 'payment', label: 'payment（内部支付）' },
                        { value: 'refund', label: 'refund（内部退款）' },
                        { value: 'voucher', label: 'voucher（券抵扣）' },
                        { value: 'wx_bill', label: 'wx_bill（微信对账行）' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    label="reason（5..200 字符）"
                    name="markReason"
                    rules={[
                      { required: true, message: '请填理由' },
                      { min: 5, max: 200, message: '5..200 字符' },
                    ]}
                  >
                    <Input.TextArea rows={2} maxLength={200} showCount />
                  </Form.Item>
                  <Button
                    type="primary"
                    onClick={submitAddMark}
                    loading={addMarkMutation.isPending}
                    data-testid="batch-mark-submit"
                  >
                    添加标记
                  </Button>
                </Form>
              </Card>
            </PermissionGate>
          </>
        ) : (
          <Empty description="加载中..." />
        )}
      </Drawer>
    </Card>
  );
}

export default function ReconciliationPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.RECONCILIATION_READ}
      fallback={
        <Card>
          <Alert
            type="error" showIcon message="无访问权限"
            description="财务对账（C7）需要 reconciliation:read 权限，仅 super_admin / finance 角色持有。"
          />
        </Card>
      }
    >
      <ReconciliationPageInner />
    </PermissionGate>
  );
}
